<template>
  <div class="i18n-page">
    <div class="i18n-header">
      <div>
        <div class="title">EN 多语言翻译处理</div>
        <div class="sub-title">使用当前登录会话处理 /language/config 未翻译数据</div>
      </div>
      <div class="actions">
        <el-button @click="loadApps" :loading="loadingApps">刷新应用</el-button>
        <el-button type="primary" @click="handleQuery" :loading="loading">{{ queryButtonText }}</el-button>
      </div>
    </div>

    <div class="i18n-filter">
      <el-form :model="form" label-width="90px">
        <el-row :gutter="14">
          <el-col :span="24">
            <el-form-item label="处理方式">
              <el-radio-group v-model="operationMode" @change="handleOperationChange">
                <el-radio-button label="supplement">未翻译处理</el-radio-button>
                <el-radio-button label="retranslate">重新翻译</el-radio-button>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="operationMode === 'supplement' ? 16 : 12">
            <el-form-item label="应用">
              <el-select
                v-model="form.appId"
                filterable
                allow-create
                default-first-option
                placeholder="请选择或输入 appId"
                style="width: 100%"
                @change="handleAppChange"
              >
                <el-option v-for="item in appList" :key="`${item.appId}`" :label="formatAppLabel(item)" :value="`${item.appId}`"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col v-if="operationMode === 'supplement'" :span="8">
            <el-form-item label="开始日期">
              <el-date-picker
                v-model="form.modifiedFrom"
                type="date"
                value-format="yyyy-MM-dd"
                placeholder="选择日期"
                style="width: 100%"
                @change="resetResult"
              ></el-date-picker>
            </el-form-item>
          </el-col>
          <el-col v-else :span="12">
            <el-form-item label="已翻译值">
              <el-input
                v-model="form.retranslateKeyword"
                clearable
                placeholder="输入需要重新翻译的英文内容"
                @keyup.enter.native="handleQuery"
                @clear="resetResult"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <div class="i18n-stats" v-if="operationMode === 'supplement'">
      <div class="stat-item">
        <span class="label">当前应用未翻译</span>
        <span class="value">{{ stats.untranslated }}</span>
      </div>
      <div class="stat-item">
        <span class="label">已过滤英文/数字</span>
        <span class="value">{{ stats.englishFiltered }}</span>
      </div>
      <div class="stat-item">
        <span class="label">日期过滤后</span>
        <span class="value">{{ stats.filtered }}</span>
      </div>
      <div class="stat-item">
        <span class="label">已匹配</span>
        <span class="value">{{ matchedList.length }}</span>
      </div>
      <div class="stat-item">
        <span class="label">待手动</span>
        <span class="value">{{ pendingList.length }}</span>
      </div>
      <div class="stat-item">
        <span class="label">冲突</span>
        <span class="value">{{ conflictList.length }}</span>
      </div>
    </div>

    <div class="i18n-stats" v-else>
      <div class="stat-item">
        <span class="label">第一步：已有翻译</span>
        <span class="value">{{ retranslateMatchedList.length }}</span>
      </div>
      <div class="stat-item">
        <span class="label">第二步：待手动翻译</span>
        <span class="value">{{ retranslatePendingList.length }}</span>
      </div>
    </div>

    <el-alert
      v-if="operationMode === 'supplement' && conflictList.length"
      class="i18n-alert"
      title="存在相同键名称但翻译值不同的历史数据，已取第一条匹配到的翻译"
      type="warning"
      :closable="false"
      show-icon
    ></el-alert>

    <div class="i18n-steps" v-if="operationMode === 'supplement'">
      <el-radio-group v-model="activeStep" size="small">
        <el-radio-button label="matched">第一步：已有翻译</el-radio-button>
        <el-radio-button label="pending">第二步：手动翻译</el-radio-button>
      </el-radio-group>
      <div class="step-actions" v-if="activeStep === 'matched'">
        <el-button icon="el-icon-document-copy" @click="copyText(matchedSourceText)">复制左侧</el-button>
        <el-button @click="matchedEditable = true" :disabled="matchedEditable">解锁右侧</el-button>
        <el-button type="primary" @click="submitMatched" :loading="submitting">提交已匹配</el-button>
      </div>
      <div class="step-actions" v-else>
        <el-button icon="el-icon-document-copy" @click="copyText(pendingSourceText)">复制左侧</el-button>
        <el-button @click="openDeepL">打开 DeepL</el-button>
        <el-button type="primary" @click="submitPending" :loading="submitting">提交手动翻译</el-button>
      </div>
    </div>

    <div class="i18n-steps" v-else>
      <el-radio-group v-model="retranslateActiveStep" size="small">
        <el-radio-button label="matched">第一步：已有翻译</el-radio-button>
        <el-radio-button label="pending">第二步：手动翻译</el-radio-button>
      </el-radio-group>
      <div class="step-actions" v-if="retranslateActiveStep === 'matched'">
        <el-button icon="el-icon-document-copy" @click="copyText(retranslateMatchedSourceText)">复制左侧</el-button>
        <el-button @click="retranslateMatchedEditable = true" :disabled="retranslateMatchedEditable">解锁右侧</el-button>
        <el-button type="primary" @click="submitRetranslatedMatched" :loading="submitting">提交已有翻译</el-button>
      </div>
      <div class="step-actions" v-else>
        <el-button icon="el-icon-document-copy" @click="copyText(retranslatePendingSourceText)">复制左侧</el-button>
        <el-button @click="openDeepL">打开 DeepL</el-button>
        <el-button type="primary" @click="submitRetranslatedPending" :loading="submitting">提交手动翻译</el-button>
      </div>
    </div>

    <div class="i18n-boxes" v-if="operationMode === 'retranslate' && retranslateActiveStep === 'matched'">
      <div class="i18n-box">
        <div class="box-title">键名称</div>
        <el-input :value="retranslateMatchedSourceText" type="textarea" readonly resize="none"></el-input>
      </div>
      <div class="i18n-box">
        <div class="box-title">参考翻译值</div>
        <el-input v-model="retranslateMatchedValueText" type="textarea" :readonly="!retranslateMatchedEditable" resize="none"></el-input>
      </div>
    </div>

    <div class="i18n-boxes" v-else-if="operationMode === 'retranslate'">
      <div class="i18n-box">
        <div class="box-title">未匹配字段</div>
        <el-input :value="retranslatePendingSourceText" type="textarea" readonly resize="none"></el-input>
      </div>
      <div class="i18n-box">
        <div class="box-title">重新翻译值</div>
        <el-input v-model="retranslatePendingValueText" type="textarea" resize="none" placeholder="从 DeepL 粘贴英文翻译，每行对应左侧一行"></el-input>
      </div>
    </div>

    <div class="i18n-boxes" v-else-if="activeStep === 'matched'">
      <div class="i18n-box">
        <div class="box-title">未翻译字段</div>
        <el-input :value="matchedSourceText" type="textarea" readonly resize="none"></el-input>
      </div>
      <div class="i18n-box">
        <div class="box-title">翻译值</div>
        <el-input v-model="matchedValueText" type="textarea" :readonly="!matchedEditable" resize="none"></el-input>
      </div>
    </div>

    <div class="i18n-boxes" v-else>
      <div class="i18n-box">
        <div class="box-title">未翻译字段</div>
        <el-input :value="pendingSourceText" type="textarea" readonly resize="none"></el-input>
      </div>
      <div class="i18n-box">
        <div class="box-title">翻译值</div>
        <el-input v-model="pendingValueText" type="textarea" resize="none" placeholder="从 DeepL 粘贴英文翻译，每行对应左侧一行"></el-input>
      </div>
    </div>

    <div class="i18n-table">
      <el-table :data="activeRows" height="260" border>
        <el-table-column prop="appId" label="当前appId" width="110"></el-table-column>
        <el-table-column v-if="operationMode === 'supplement' && activeStep === 'matched'" prop="matchAppId" label="来源appId" width="110"></el-table-column>
        <el-table-column v-if="operationMode === 'retranslate' && retranslateActiveStep === 'matched'" prop="matchAppId" label="参考appId" width="110"></el-table-column>
        <el-table-column prop="langKeyName" label="键名称" min-width="220"></el-table-column>
        <el-table-column prop="dataValue" label="翻译值" min-width="260"></el-table-column>
        <el-table-column prop="gmtModified" label="修改时间" width="170"></el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import apis from '@/utils/apis';

const LANG_CODE = 'EN';
const PAGE_SIZE = 500;
const REQUEST_CONCURRENCY = 6;

function getCurrentMonthStart() {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');

  return `${now.getFullYear()}-${month}-01`;
}

function getRowValue(row) {
  if (row.dataValue === null || row.dataValue === undefined) {
    return '';
  }
  return `${row.dataValue}`.trim();
}

function uniqueList(list) {
  return [...new Set(list.filter(Boolean))];
}

function isEnglishKeyName(value) {
  const text = `${value || ''}`.trim();

  if (!text) {
    return false;
  }

  return (/[A-Za-z]/).test(text) && !(/[\u3400-\u9FFF\uF900-\uFAFF]/).test(text);
}

function isNumberKeyName(value) {
  const text = `${value || ''}`.trim();

  return (/^\d+$/).test(text);
}

export default {
  name: 'i18n',
  data() {
    return {
      form: {
        appId: '',
        modifiedFrom: getCurrentMonthStart(),
        retranslateKeyword: '',
      },
      appList: [],
      operationMode: 'supplement',
      activeStep: 'matched',
      matchedList: [],
      pendingList: [],
      conflictList: [],
      retranslateMatchedList: [],
      retranslatePendingList: [],
      retranslateActiveStep: 'matched',
      matchedValueText: '',
      matchedEditable: false,
      pendingValueText: '',
      retranslateMatchedValueText: '',
      retranslateMatchedEditable: false,
      retranslatePendingValueText: '',
      loading: false,
      loadingApps: false,
      submitting: false,
      stats: {
        untranslated: 0,
        englishFiltered: 0,
        filtered: 0,
      },
    };
  },
  computed: {
    queryButtonText() {
      return this.operationMode === 'supplement' ? '查询匹配' : '查询已翻译';
    },
    matchedSourceText() {
      return this.matchedList.map(item => item.langKeyName || '').join('\n');
    },
    pendingSourceText() {
      return this.pendingList.map(item => item.langKeyName || '').join('\n');
    },
    retranslateMatchedSourceText() {
      return this.retranslateMatchedList.map(item => item.langKeyName || '').join('\n');
    },
    retranslatePendingSourceText() {
      return this.retranslatePendingList.map(item => item.langKeyName || '').join('\n');
    },
    activeRows() {
      if (this.operationMode === 'retranslate') {
        return this.retranslateActiveStep === 'matched' ? this.retranslateMatchedPreviewRows() : this.retranslatePendingPreviewRows();
      }
      return this.activeStep === 'matched' ? this.matchedPreviewRows() : this.pendingPreviewRows();
    },
  },
  mounted() {
    this.loadApps();
  },
  methods: {
    async requestGet(url, params) {
      const res = await this.$getJSON(url, params);

      return res.data;
    },
    async requestPost(url, data) {
      const res = await this.$postJSON(url, data || {});

      return res.data;
    },
    formatAppLabel(item) {
      if (!item.appName) {
        return `${item.appId}`;
      }
      return `${item.appName}（${item.appId}）`;
    },
    handleAppChange() {
      this.resetResult();
      if (this.operationMode === 'supplement' && this.form.appId && this.form.modifiedFrom) {
        this.analyseLanguage();
      }
    },
    handleOperationChange() {
      this.resetResult();
    },
    handleQuery() {
      if (this.operationMode === 'retranslate') {
        this.searchRetranslated();
        return;
      }
      this.analyseLanguage();
    },
    resetResult() {
      this.matchedList = [];
      this.pendingList = [];
      this.conflictList = [];
      this.retranslateMatchedList = [];
      this.retranslatePendingList = [];
      this.retranslateActiveStep = 'matched';
      this.matchedValueText = '';
      this.matchedEditable = false;
      this.pendingValueText = '';
      this.retranslateMatchedValueText = '';
      this.retranslateMatchedEditable = false;
      this.retranslatePendingValueText = '';
      this.stats.untranslated = 0;
      this.stats.englishFiltered = 0;
      this.stats.filtered = 0;
    },
    checkForm() {
      if (!this.form.appId) {
        this.$message.warning('请选择或输入应用');
        return false;
      }
      if (!this.form.modifiedFrom) {
        this.$message.warning('请选择开始日期');
        return false;
      }
      return true;
    },
    checkRetranslateForm() {
      if (!this.form.appId) {
        this.$message.warning('请选择或输入应用');
        return false;
      }
      if (!this.form.retranslateKeyword.trim()) {
        this.$message.warning('请输入需要查找的已翻译值');
        return false;
      }
      return true;
    },
    async loadApps() {
      this.loadingApps = true;
      try {
        const data = await this.requestGet(apis.AppMenu);

        this.appList = Array.isArray(data) ? data : [];
      } catch (err) {
        this.$message.error(err.message || '加载应用失败');
      } finally {
        this.loadingApps = false;
      }
    },
    async analyseLanguage() {
      if (!this.checkForm()) {
        return;
      }
      this.loading = true;
      this.resetResult();
      try {
        const targetAppId = `${this.form.appId}`;
        const allAppIds = await this.getAllAppIds(targetAppId);
        const untranslatedRows = await this.fetchRowsByApps([targetAppId], 0);
        const needTranslateRows = untranslatedRows.filter(row => !isEnglishKeyName(row.langKeyName) && !isNumberKeyName(row.langKeyName));
        const filteredRows = this.filterRowsByModifiedFrom(needTranslateRows);
        const translatedRows = await this.fetchRowsByApps(allAppIds, 1);
        const index = this.buildTranslatedIndex(translatedRows);

        this.stats.untranslated = untranslatedRows.length;
        this.stats.englishFiltered = untranslatedRows.length - needTranslateRows.length;
        this.stats.filtered = filteredRows.length;

        filteredRows.forEach((row) => {
          const result = this.findTranslated(row, index);

          if (result.conflict) {
            this.conflictList.push(row);
          }
          if (result.match) {
            this.matchedList.push({
              ...row,
              dataValue: result.match.value,
              matchAppId: result.match.row.appId,
            });
            return;
          }
          this.pendingList.push({
            ...row,
            dataValue: '',
          });
        });

        this.matchedValueText = this.matchedList.map(item => item.dataValue || '').join('\n');
        this.activeStep = this.matchedList.length ? 'matched' : 'pending';
        this.$message.success('查询完成');
      } catch (err) {
        this.$message.error(err.message || '查询失败');
      } finally {
        this.loading = false;
      }
    },
    async searchRetranslated() {
      if (!this.checkRetranslateForm()) {
        return;
      }

      this.loading = true;
      this.resetResult();
      try {
        const targetAppId = `${this.form.appId}`;
        const keyword = this.form.retranslateKeyword.trim().toLowerCase();
        const translatedRows = await this.fetchRowsByApps([targetAppId], 1);
        const allAppIds = await this.getAllAppIds(targetAppId);
        const referenceRows = await this.fetchRowsByApps(allAppIds, 1);
        const referenceIndex = this.buildTranslatedIndex(referenceRows.filter(row => `${row.appId}` !== targetAppId));

        // 接口未提供翻译值筛选参数，按当前模块的已翻译数据在前端筛选。
        const matchedRows = [];
        const pendingRows = [];

        translatedRows
          .filter(row => getRowValue(row).toLowerCase().includes(keyword))
          .forEach((row) => {
            const result = this.findReferenceTranslated(row, referenceIndex);

            const item = {
              ...row,
              dataValue: result.match ? result.match.value : '',
              matchAppId: result.match ? result.match.row.appId : '',
            };

            if (result.match) {
              matchedRows.push(item);
              return;
            }
            pendingRows.push(item);
          });
        this.retranslateMatchedList = matchedRows;
        this.retranslatePendingList = pendingRows;
        this.retranslateMatchedValueText = matchedRows.map(item => item.dataValue || '').join('\n');
        this.retranslateActiveStep = matchedRows.length ? 'matched' : 'pending';
        this.$message.success(`查询完成：已有翻译${matchedRows.length}条，待手动翻译${pendingRows.length}条`);
      } catch (err) {
        this.$message.error(err.message || '查询失败');
      } finally {
        this.loading = false;
      }
    },
    async getAllAppIds(targetAppId) {
      if (!this.appList.length) {
        try {
          await this.loadApps();
        } catch (err) {
          return [targetAppId];
        }
      }
      const appIds = this.appList.map(item => `${item.appId}`).filter(Boolean);

      return uniqueList([targetAppId, ...appIds]);
    },
    async fetchRowsByApps(appIds, translateStatus) {
      const rows = [];
      const ids = uniqueList(appIds.map(item => `${item}`));

      for (let i = 0; i < ids.length; i += REQUEST_CONCURRENCY) {
        const batchAppIds = ids.slice(i, i + REQUEST_CONCURRENCY);
        const batchRows = await Promise.all(batchAppIds.map(async (appId) => {
          const list = await this.fetchAllLanguageRows({
            appId,
            langCode: LANG_CODE,
            translateStatus,
          });

          return list.map(item => ({
            ...item,
            appId: `${item.appId || appId}`,
          }));
        }));

        batchRows.forEach((list) => {
          rows.push(...list);
        });
      }

      return rows;
    },
    async fetchAllLanguageRows(params) {
      const firstPage = await this.fetchLanguagePage(params, 1);
      const result = firstPage.list;
      const total = firstPage.total;
      const totalPages = Math.ceil(total / PAGE_SIZE);

      if (totalPages <= 1) {
        return result;
      }

      const pageNums = [];

      for (let pageNum = 2; pageNum <= totalPages; pageNum++) {
        pageNums.push(pageNum);
      }

      for (let i = 0; i < pageNums.length; i += REQUEST_CONCURRENCY) {
        const batchPageNums = pageNums.slice(i, i + REQUEST_CONCURRENCY);
        const batchPages = await Promise.all(batchPageNums.map(pageNum => this.fetchLanguagePage(params, pageNum)));

        batchPages.forEach((page) => {
          result.push(...page.list);
        });
      }

      return result;
    },
    async fetchLanguagePage(params, pageNum) {
      const data = await this.requestGet(apis.LanguagePage, {
        ...params,
        pageSize: PAGE_SIZE,
        pageNum,
      });

      return {
        list: data && data.list ? data.list : [],
        total: Number(data && data.total) || 0,
      };
    },
    filterRowsByModifiedFrom(rows) {
      const startTime = new Date(`${this.form.modifiedFrom}T00:00:00`).getTime();

      return rows.filter((row) => {
        const time = new Date(row.gmtModified).getTime();
        return !Number.isNaN(time) && time >= startTime;
      });
    },
    buildTranslatedIndex(rows) {
      const byApp = new Map();
      const byName = new Map();

      rows.forEach((row) => {
        const appId = `${row.appId || ''}`;
        const langKeyName = row.langKeyName;
        const value = getRowValue(row);

        if (!appId || !langKeyName || !value) {
          return;
        }

        this.setIndexValue(byApp, `${appId}\u0000${langKeyName}`, row, value);
        this.setIndexValue(byName, langKeyName, row, value);
      });

      return {
        byApp,
        byName,
      };
    },
    setIndexValue(map, key, row, value) {
      const current = map.get(key);

      if (!current) {
        map.set(key, {
          row,
          value,
          conflict: false,
          values: new Set([value]),
        });
        return;
      }

      current.values.add(value);
      if (current.values.size > 1) {
        current.conflict = true;
      }
    },
    findTranslated(row, index) {
      const appKey = `${row.appId}\u0000${row.langKeyName}`;
      const currentAppMatch = index.byApp.get(appKey);

      if (currentAppMatch) {
        return {
          match: currentAppMatch,
          conflict: currentAppMatch.conflict,
        };
      }

      const crossAppMatch = index.byName.get(row.langKeyName);

      if (crossAppMatch) {
        return {
          match: crossAppMatch,
          conflict: crossAppMatch.conflict,
        };
      }

      return {};
    },
    findReferenceTranslated(row, index) {
      const match = index.byName.get(row.langKeyName);

      if (!match) {
        return {};
      }
      return { match };
    },
    buildPayload(row, dataValue) {
      return {
        langKeyId: row.langKeyId,
        langCode: LANG_CODE,
        dataValue: `${dataValue || ''}`.trim(),
        langKeyDesc: row.langKeyDesc || '',
      };
    },
    async submitMatched() {
      const rows = this.matchedPreviewRows().filter(item => getRowValue(item));
      await this.submitRows(rows, '已匹配翻译');
    },
    async submitPending() {
      const rows = this.pendingPreviewRows().filter(item => getRowValue(item));

      if (!rows.length) {
        this.$message.warning('请先填写翻译值');
        return;
      }
      if (rows.length < this.pendingList.length) {
        try {
          await this.$confirm('翻译值行数少于未翻译字段，只会提交已填写的内容，是否继续？', '提示', {
            type: 'warning',
          });
        } catch (err) {
          return;
        }
      }

      await this.submitRows(rows, '手动翻译', true);
    },
    async submitRetranslatedMatched() {
      const rows = this.retranslateMatchedPreviewRows().filter(item => getRowValue(item));

      if (!rows.length) {
        this.$message.warning('没有可提交的已有翻译');
        return;
      }

      await this.submitRows(rows, '重新翻译已有翻译');
    },
    async submitRetranslatedPending() {
      const rows = this.retranslatePendingPreviewRows().filter(item => getRowValue(item));

      if (!rows.length) {
        this.$message.warning('请先填写重新翻译值');
        return;
      }
      if (rows.length < this.retranslatePendingList.length) {
        try {
          await this.$confirm('重新翻译值行数少于未匹配字段，只会提交已填写的内容，是否继续？', '提示', {
            type: 'warning',
          });
        } catch (err) {
          return;
        }
      }

      await this.submitRows(rows, '重新翻译手动翻译');
    },
    pendingPreviewRows() {
      const values = this.pendingValueText.split(/\r?\n/);

      return this.pendingList.map((row, index) => ({
        ...row,
        dataValue: values[index] || '',
      }));
    },
    matchedPreviewRows() {
      const values = this.matchedValueText.split(/\r?\n/);

      return this.matchedList.map((row, index) => ({
        ...row,
        dataValue: values[index] || '',
      }));
    },
    retranslateMatchedPreviewRows() {
      const values = this.retranslateMatchedValueText.split(/\r?\n/);

      return this.retranslateMatchedList.map((row, index) => ({
        ...row,
        dataValue: values[index] || '',
      }));
    },
    retranslatePendingPreviewRows() {
      const values = this.retranslatePendingValueText.split(/\r?\n/);

      return this.retranslatePendingList.map((row, index) => ({
        ...row,
        dataValue: values[index] || '',
      }));
    },
    async submitRows(rows, label, autoNextApp) {
      const payloads = rows
        .map(row => this.buildPayload(row, row.dataValue))
        .filter(item => item.langKeyId && item.dataValue);

      if (!payloads.length) {
        this.$message.warning('没有可提交的数据');
        return;
      }

      this.submitting = true;
      try {
        for (const payload of payloads) {
          await this.requestPost(apis.LanguageSetup, payload);
        }

        this.$message.success(`${label}提交成功：${payloads.length}条`);
        if (autoNextApp && this.switchToNextApp()) {
          await this.analyseLanguage();
          return;
        }
        if (autoNextApp) {
          this.$message.info('已经是最后一个应用');
        }
        await this.handleQuery();
      } catch (err) {
        this.$message.error(err.message || '提交失败');
      } finally {
        this.submitting = false;
      }
    },
    switchToNextApp() {
      const appIds = uniqueList(this.appList.map(item => `${item.appId}`).filter(Boolean));
      const currentIndex = appIds.indexOf(`${this.form.appId}`);

      if (currentIndex < 0 || currentIndex >= appIds.length - 1) {
        return false;
      }
      this.form.appId = appIds[currentIndex + 1];
      return true;
    },
    copyText(text) {
      if (!text) {
        this.$message.warning('没有可复制的内容');
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success('已复制');
        }).catch(() => {
          this.copyByTextarea(text);
        });
        return;
      }
      this.copyByTextarea(text);
    },
    copyByTextarea(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.$message.success('已复制');
    },
    openDeepL() {
      window.open('https://www.deepl.com/en/translator', '_blank');
    },
  },
};
</script>

<style lang="scss">
.i18n-page {
  min-height: 100vh;
  padding: 18px;
  background: #f5f7fb;
  color: #303133;

  .i18n-header,
  .i18n-filter,
  .i18n-stats,
  .i18n-steps,
  .i18n-table {
    background: #ffffff;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
  }

  .i18n-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    margin-bottom: 12px;

    .title {
      font-size: 18px;
      line-height: 26px;
      font-weight: bold;
    }

    .sub-title {
      color: #909399;
      font-size: 12px;
      line-height: 18px;
    }
  }

  .i18n-filter {
    padding: 16px 16px 2px;
    margin-bottom: 12px;
  }

  .i18n-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 16px;
    margin-bottom: 12px;

    .stat-item {
      min-width: 120px;

      .label {
        color: #909399;
        margin-right: 6px;
      }

      .value {
        font-weight: bold;
      }
    }
  }

  .i18n-alert {
    margin-bottom: 12px;
  }

  .i18n-steps {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    margin-bottom: 12px;
  }

  .i18n-boxes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;

    .i18n-box {
      background: #ffffff;
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      padding: 12px;

      .box-title {
        font-weight: bold;
        line-height: 22px;
        margin-bottom: 8px;
      }

      .el-textarea__inner {
        height: 300px;
        line-height: 20px;
        font-family: Consolas, 'Courier New', monospace;
      }
    }
  }

  .i18n-table {
    padding: 12px;
  }
}
</style>
